import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from '~/components/Link';
import MetaTags from 'react-meta-tags';

import { ListItemForDeletion } from '~/components/Modals/components/ListItemCard';
import type { ImgDefinition } from '~/graphql/types.client.flow';
import { OfficeInList } from '~/scenes/Settings/Profile/components/FormContainer';

import UserProfileHeader from '~/components/ProfileHeader/UserProfileHeader';
import { UserDetailsForm } from '~/components';
import Button from '~/components/Button';
import cleanedFilename from '~/util/cleanedFilename';
import ContentContainerDefault from '~/components/ContentContainer/Default';
import DeleteUserFromOfficeModal from '~/components/Modals/DeleteUserFromOfficeModal';
import DeleteUserFromAccountModal from '~/components/Modals/DeleteUserFromAccountModal';
import DetailsActionList from '~/components/DetailsPage/DetailsActionList';
import {
  ActionButtons,
  Header,
  SectionContainer,
} from '~/scenes/Settings/Profile/Profile.style';
import { convertOfficeInListToListItemForDeletion } from '~/scenes/Settings/Users/utils';
import {
  NotificationSettings,
  Signature,
  useUpdateUserMutation,
} from '~/graphql/types';
import SignatureContainer from '~/components/SignatureContainer';
import useCurrentUser from '~/hooks/useCurrentUser';
import EmailNotificationsBlock, {
  EmailNotificationsFormData,
} from '~/components/EmailNotificationsBlock';
import { emailNotificationsFormDataToMutation } from '~/components/EmailNotificationsBlock/util';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import { createOfficeItemForModal } from '../Profile/utils/createOfficeItemForModal';
import useUserRights from '~/hooks/useUserRights';

type Props = {
  user: {
    name: string;
    id: string;
    img?: ImgDefinition | null | undefined;
  };
  officesList: Array<OfficeInList>;
  initialValue: { email: string; phone: string };
  userIsBeingDeletedFromOffice: boolean;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  refetch: () => Promise<any>;
  signature: Signature | null;
  notificationSettings?: NotificationSettings;
};

const text = {
  pageTitle: 'Gebruiker',
  editYourProfile: 'Wijzig je gegevens',
  userListBreadcrumbTitle: 'Gebruikerslijst',
  deleteUser: 'Verwijder gebruiker',
  signature: 'Handtekening gebruiker',
};

const UserDetailsComponent = ({
  user,
  userIsBeingDeletedFromOffice,
  initialValue,
  officesList,
  startPolling,
  stopPolling,
  refetch,
  signature,
  notificationSettings,
}: Props) => {
  const { update: mayUpdate, delete: mayDelete } = useUserRights({
    category: 'Users',
  });

  const { id: accountId } = useCurrentAccount();
  const me = useCurrentUser();
  const addToast = useAddToast();
  const isPolling = useRef(false);

  const isItMe = user.id === me.id;
  const mayEdit = isItMe || mayUpdate;

  const [showDeleteUserFromOfficeModal, setShowDeleteUserFromOfficeModal] =
    useState<Array<ListItemForDeletion> | null>(null);
  const [showDeleteUserFromAccountModal, setShowDeleteUserFromAccountModal] =
    useState<Array<ListItemForDeletion> | null>(null);

  const [updateUser] = useUpdateUserMutation({
    onCompleted: _update => {
      if (_update) return refetch();
      return;
    },
  });

  const onFormSubmit = (newFormData: EmailNotificationsFormData) =>
    updateUser({
      variables: {
        accountId,
        userId: user.id,
        update: emailNotificationsFormDataToMutation(newFormData),
      },
    }).then(({ errors }) => {
      if (errors && errors.length !== 0) {
        addToast([
          formatToastMessage(
            'Er is iets fout gegaan bij het updaten van notificatie instellingen, probeer het later opnieuw.',
            'danger',
          ),
        ]);
        return;
      }
    });

  const handleDeleteUserFromOfficeModalAction = (
    officesList: Array<ListItemForDeletion> | null,
  ) => {
    setShowDeleteUserFromOfficeModal(officesList);
  };

  const handleDeleteUserFromAccountModalAction = (
    officesList: Array<ListItemForDeletion> | null,
  ) => {
    setShowDeleteUserFromAccountModal(officesList);
  };

  const offices = officesList.map(convertOfficeInListToListItemForDeletion);

  if (userIsBeingDeletedFromOffice) {
    if (isPolling.current === false) {
      startPolling(3000);
      isPolling.current = true;
    }
  }

  if (!userIsBeingDeletedFromOffice) {
    if (isPolling.current === true) {
      stopPolling();
      isPolling.current = false;
    }
  }

  return (
    <ContentContainerDefault
      data-testid="user-detail-page"
      breadcrumbs={[
        {
          to: '/-/settings/users',
          label: text.userListBreadcrumbTitle,
        },
        {
          label: user.name,
        },
      ]}
    >
      <MetaTags>
        <title>{user.name}</title>
      </MetaTags>
      <Header>
        <UserProfileHeader
          edit={isItMe}
          user={user}
          refetchUser={() => {}}
          onImageUploadError={() => {
            throw Error(
              `${cleanedFilename(
                __filename,
              )} | Should not occur | onImageUploadError was called`,
            );
          }}
          updateMyName={() => {
            throw Error(
              `${cleanedFilename(
                __filename,
              )} | Should not occur | updateMyName was called`,
            );
          }}
        />

        {isItMe && (
          <ProfileLink to={'/-/settings/profile'}>
            <Button
              ghost
              onClick={() => {}}
              data-testid="edit-profile-link"
              label={text.editYourProfile}
            />
          </ProfileLink>
        )}

        {mayDelete && (
          <ActionButtons>
            <DetailsActionList
              options={[
                {
                  label: text.deleteUser,
                  onClick: () => {
                    handleDeleteUserFromAccountModalAction(offices);
                  },
                  type: 'DELETE',
                  key: 'delete-user',
                },
              ]}
            />
          </ActionButtons>
        )}
      </Header>

      <SectionContainer>
        <div>
          <UserDetailsForm
            officesList={officesList}
            readOnly
            showDeleteModal={officeId =>
              handleDeleteUserFromOfficeModalAction([
                createOfficeItemForModal(offices, officeId),
              ])
            }
            mayEdit={mayEdit}
            userId={user.id}
            dataIsPolling={isPolling.current}
            initialValues={initialValue}
          />
          <SignatureContainer
            title={text.signature}
            signature={signature}
            entityTypeForMutation={{ userId: user.id }}
            refetch={refetch}
            readOnly={!mayEdit}
          />
        </div>

        {mayEdit && notificationSettings && (
          <EmailNotificationsBlock
            onFormSubmit={onFormSubmit}
            notificationSettings={notificationSettings}
          />
        )}
      </SectionContainer>

      {showDeleteUserFromOfficeModal && (
        <DeleteUserFromOfficeModal
          list={showDeleteUserFromOfficeModal}
          onCancel={() => handleDeleteUserFromOfficeModalAction(null)}
          onClose={() => handleDeleteUserFromOfficeModalAction(null)}
          refetch={refetch}
          userId={user.id}
        />
      )}

      {showDeleteUserFromAccountModal && (
        <DeleteUserFromAccountModal
          list={showDeleteUserFromAccountModal}
          onCancel={() => handleDeleteUserFromAccountModalAction(null)}
          onClose={() => handleDeleteUserFromAccountModalAction(null)}
          userId={user.id}
        />
      )}
    </ContentContainerDefault>
  );
};

const ProfileLink = styled(Link)<{}>(
  ({ theme }) => css`
    margin-left: auto;
    order: 2;
    text-decoration: none;

    ${theme.mq.lessThan('tabletLandscape')`
       order: 2;
    `}

    button {
      width: max-content;
    }
  `,
);

export default UserDetailsComponent;
